import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, FileText, RefreshCw, Check } from 'lucide-react';
import {
  useAllPageSeo,
  useCreatePageSeo,
  useUpdatePageSeo,
  useDeletePageSeo,
  PageSeo,
} from '@/hooks/useSiteSettings';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
  ImageUploadField,
} from '@/components/admin/shared';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PAGE_REGISTRY, PageDefinition } from '@/lib/seo/page-registry';
import type { Json } from '@/integrations/supabase/types';

export function SeoManager() {
  const { data: pages, isLoading } = useAllPageSeo();
  const createPage = useCreatePageSeo();
  const updatePage = useUpdatePageSeo();
  const deletePage = useDeletePageSeo();
  
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isSyncSheetOpen, setIsSyncSheetOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<PageSeo | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<PageSeo | null>(null);
  const [selectedSyncPages, setSelectedSyncPages] = useState<Set<string>>(new Set());
  const [isSyncing, setIsSyncing] = useState(false);
  
  const [form, setForm] = useState({
    path: '',
    title: '',
    description: '',
    og_title: '',
    og_description: '',
    og_image: '',
    canonical_url: '',
    no_index: false,
    include_in_sitemap: true,
    priority: 0.5,
    change_freq: 'weekly',
    json_ld: '',
  });

  // Calculate missing pages by comparing registry with existing pages
  const { missingPages, existingPaths } = useMemo(() => {
    const existingPaths = new Set(pages?.map(p => p.path) || []);
    const missingPages = PAGE_REGISTRY.filter(p => !existingPaths.has(p.path));
    return { missingPages, existingPaths };
  }, [pages]);

  const resetForm = () => {
    setForm({
      path: '',
      title: '',
      description: '',
      og_title: '',
      og_description: '',
      og_image: '',
      canonical_url: '',
      no_index: false,
      include_in_sitemap: true,
      priority: 0.5,
      change_freq: 'weekly',
      json_ld: '',
    });
    setEditingPage(null);
  };

  const handleEdit = (page: PageSeo) => {
    setEditingPage(page);
    setForm({
      path: page.path,
      title: page.title || '',
      description: page.description || '',
      og_title: page.og_title || '',
      og_description: page.og_description || '',
      og_image: page.og_image || '',
      canonical_url: page.canonical_url || '',
      no_index: page.no_index,
      include_in_sitemap: page.include_in_sitemap,
      priority: page.priority,
      change_freq: page.change_freq,
      json_ld: page.json_ld ? JSON.stringify(page.json_ld, null, 2) : '',
    });
    setIsSheetOpen(true);
  };

  const handleSubmit = async () => {
    try {
      let parsedJsonLd: Json | null = null;
      if (form.json_ld) {
        try {
          parsedJsonLd = JSON.parse(form.json_ld) as Json;
        } catch {
          toast.error('Invalid JSON-LD format');
          return;
        }
      }

      const data = {
        path: form.path,
        title: form.title || null,
        description: form.description || null,
        og_title: form.og_title || null,
        og_description: form.og_description || null,
        og_image: form.og_image || null,
        canonical_url: form.canonical_url || null,
        no_index: form.no_index,
        include_in_sitemap: form.include_in_sitemap,
        priority: form.priority,
        change_freq: form.change_freq,
        json_ld: parsedJsonLd,
        display_order: 0,
      };

      if (editingPage) {
        await updatePage.mutateAsync({ id: editingPage.id, ...data });
        toast.success('Page SEO updated');
      } else {
        await createPage.mutateAsync(data);
        toast.success('Page SEO created');
      }
      
      setIsSheetOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Failed to save page SEO');
    }
  };

  const handleDelete = async () => {
    if (!pageToDelete) return;
    try {
      await deletePage.mutateAsync(pageToDelete.id);
      toast.success('Page SEO deleted');
      setDeleteDialogOpen(false);
      setPageToDelete(null);
    } catch (error) {
      toast.error('Failed to delete page SEO');
    }
  };

  const handleOpenSyncSheet = () => {
    // Pre-select all missing pages
    setSelectedSyncPages(new Set(missingPages.map(p => p.path)));
    setIsSyncSheetOpen(true);
  };

  const toggleSyncPage = (path: string) => {
    setSelectedSyncPages(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const toggleAllSyncPages = () => {
    if (selectedSyncPages.size === missingPages.length) {
      setSelectedSyncPages(new Set());
    } else {
      setSelectedSyncPages(new Set(missingPages.map(p => p.path)));
    }
  };

  const handleBulkSync = async () => {
    const pagesToSync = missingPages.filter(p => selectedSyncPages.has(p.path));
    if (pagesToSync.length === 0) {
      toast.error('No pages selected');
      return;
    }

    setIsSyncing(true);
    let successCount = 0;
    let errorCount = 0;

    for (const page of pagesToSync) {
      try {
        await createPage.mutateAsync({
          path: page.path,
          title: page.title,
          description: page.description,
          priority: page.priority ?? 0.5,
          change_freq: page.changeFreq ?? 'weekly',
          include_in_sitemap: page.includeInSitemap ?? true,
          no_index: page.noIndex ?? false,
          og_title: null,
          og_description: null,
          og_image: null,
          canonical_url: null,
          json_ld: null,
          display_order: 0,
        });
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to sync ${page.path}:`, error);
      }
    }

    setIsSyncing(false);
    setIsSyncSheetOpen(false);
    setSelectedSyncPages(new Set());

    if (errorCount === 0) {
      toast.success(`Added ${successCount} page${successCount !== 1 ? 's' : ''} to SEO table`);
    } else {
      toast.warning(`Added ${successCount} pages, ${errorCount} failed`);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <AdminPageHeader title="Page SEO" />
        <AdminTableSkeleton columns={5} rows={7} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader 
        title="Page SEO" 
        actions={
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleOpenSyncSheet}
              disabled={missingPages.length === 0}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Sync Pages
              {missingPages.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {missingPages.length}
                </Badge>
              )}
            </Button>
            <Button onClick={() => { resetForm(); setIsSheetOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Add Page
            </Button>
          </div>
        }
      />

      {pages && pages.length > 0 ? (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Path</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Sitemap</TableHead>
                <TableHead>NoIndex</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((page) => (
                <TableRow 
                  key={page.id} 
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleEdit(page)}
                >
                  <TableCell className="font-mono text-sm">{page.path}</TableCell>
                  <TableCell className="max-w-xs truncate">{page.title || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={page.include_in_sitemap ? 'default' : 'secondary'}>
                      {page.include_in_sitemap ? 'Yes' : 'No'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {page.no_index && <Badge variant="destructive">NoIndex</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div 
                      className="flex justify-end gap-2" 
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(page)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setPageToDelete(page); setDeleteDialogOpen(true); }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <AdminEmptyState
          icon={FileText}
          title="No pages configured"
          description="Add page-specific SEO settings"
          actionLabel="Add Page"
          onAction={() => { resetForm(); setIsSheetOpen(true); }}
        />
      )}

      {/* Edit/Create Page Sheet */}
      <AdminSheet
        open={isSheetOpen}
        onOpenChange={(open) => { setIsSheetOpen(open); if (!open) resetForm(); }}
        title={editingPage ? 'Edit Page SEO' : 'Add Page SEO'}
        description="Configure SEO settings for this page"
        onSave={handleSubmit}
        isSaving={createPage.isPending || updatePage.isPending}
        saveLabel={editingPage ? 'Update' : 'Create'}
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="path">Path *</Label>
            <Input
              id="path"
              value={form.path}
              onChange={(e) => setForm(prev => ({ ...prev, path: e.target.value }))}
              placeholder="/about"
              disabled={!!editingPage}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="page_title">Title</Label>
            <Input
              id="page_title"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="About | AutomotiveAPI"
            />
            <p className="text-xs text-muted-foreground">Keep under 60 characters</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="page_description">Meta Description</Label>
            <Textarea
              id="page_description"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Learn about the mission behind AutomotiveAPI..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">Keep under 160 characters</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="og_title">OG Title (optional override)</Label>
            <Input
              id="og_title"
              value={form.og_title}
              onChange={(e) => setForm(prev => ({ ...prev, og_title: e.target.value }))}
              placeholder="Leave empty to use page title"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="og_description">OG Description (optional override)</Label>
            <Textarea
              id="og_description"
              value={form.og_description}
              onChange={(e) => setForm(prev => ({ ...prev, og_description: e.target.value }))}
              placeholder="Leave empty to use meta description"
              rows={2}
            />
          </div>
          
          <ImageUploadField
            value={form.og_image}
            onChange={(url) => setForm(prev => ({ ...prev, og_image: url }))}
            label="OG Image"
            description="Page-specific social sharing image (optional, falls back to default)"
            storageBucket="seo-images"
            storageFolder="pages"
          />
          
          <div className="space-y-2">
            <Label htmlFor="canonical_url">Canonical URL (optional)</Label>
            <Input
              id="canonical_url"
              value={form.canonical_url}
              onChange={(e) => setForm(prev => ({ ...prev, canonical_url: e.target.value }))}
              placeholder="https://automotiveapi.io/about"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="priority">Sitemap Priority</Label>
              <Select
                value={form.priority.toString()}
                onValueChange={(value) => setForm(prev => ({ ...prev, priority: parseFloat(value) }))}
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1.0">1.0 (Highest)</SelectItem>
                  <SelectItem value="0.9">0.9</SelectItem>
                  <SelectItem value="0.8">0.8</SelectItem>
                  <SelectItem value="0.7">0.7</SelectItem>
                  <SelectItem value="0.6">0.6</SelectItem>
                  <SelectItem value="0.5">0.5 (Default)</SelectItem>
                  <SelectItem value="0.4">0.4</SelectItem>
                  <SelectItem value="0.3">0.3</SelectItem>
                  <SelectItem value="0.2">0.2</SelectItem>
                  <SelectItem value="0.1">0.1 (Lowest)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="change_freq">Change Frequency</Label>
              <Select
                value={form.change_freq}
                onValueChange={(value) => setForm(prev => ({ ...prev, change_freq: value }))}
              >
                <SelectTrigger id="change_freq">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="always">Always</SelectItem>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="never">Never</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center justify-between py-2">
            <div className="space-y-0.5">
              <Label>Include in Sitemap</Label>
              <p className="text-xs text-muted-foreground">Show this page in the XML sitemap</p>
            </div>
            <Switch
              checked={form.include_in_sitemap}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, include_in_sitemap: checked }))}
            />
          </div>
          
          <div className="flex items-center justify-between py-2">
            <div className="space-y-0.5">
              <Label>NoIndex</Label>
              <p className="text-xs text-muted-foreground">Prevent search engines from indexing</p>
            </div>
            <Switch
              checked={form.no_index}
              onCheckedChange={(checked) => setForm(prev => ({ ...prev, no_index: checked }))}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="json_ld">Custom JSON-LD (optional)</Label>
            <Textarea
              id="json_ld"
              value={form.json_ld}
              onChange={(e) => setForm(prev => ({ ...prev, json_ld: e.target.value }))}
              placeholder='{"@type": "FAQPage", ...}'
              rows={4}
              className="font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">Additional structured data to merge with WebPage schema</p>
          </div>
        </div>
      </AdminSheet>

      {/* Sync Pages Sheet */}
      <AdminSheet
        open={isSyncSheetOpen}
        onOpenChange={setIsSyncSheetOpen}
        title="Sync Missing Pages"
        description="Add pages from the registry that aren't in the SEO table yet"
        onSave={handleBulkSync}
        isSaving={isSyncing}
        saveLabel={`Add ${selectedSyncPages.size} Page${selectedSyncPages.size !== 1 ? 's' : ''}`}
        saveDisabled={selectedSyncPages.size === 0}
      >
        <div className="space-y-4">
          {missingPages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Check className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium">All pages are synced!</p>
              <p className="text-sm">Every page in the registry has SEO configuration.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 pb-2 border-b">
                <Checkbox
                  id="select-all"
                  checked={selectedSyncPages.size === missingPages.length}
                  onCheckedChange={toggleAllSyncPages}
                />
                <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  Select all ({missingPages.length} pages)
                </Label>
              </div>

              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {missingPages.map((page) => (
                  <div
                    key={page.path}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedSyncPages.has(page.path)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/50'
                    }`}
                    onClick={() => toggleSyncPage(page.path)}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedSyncPages.has(page.path)}
                        onCheckedChange={() => toggleSyncPage(page.path)}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-sm font-medium">{page.path}</p>
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                          {page.title}
                        </p>
                        <div className="flex gap-3 mt-1.5 text-xs text-muted-foreground">
                          <span>Priority: {page.priority ?? 0.5}</span>
                          <span>Change: {page.changeFreq ?? 'weekly'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </AdminSheet>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        itemName={pageToDelete?.path}
        itemType="Page SEO"
        isDeleting={deletePage.isPending}
      />
    </div>
  );
}
